/* eslint-disable sort-keys */
import assignmentManager, { ASSIGNMENT_STATUS } from '../managers/AssignmentManager';
import classroomManager from '../managers/ClassroomManager';
import contentManager, { CONTENT_MODE, PLAYER_TYPES } from '../managers/ContentManager';
import dialogManager, { DIALOG_NAMES } from '../managers/DialogManager';
import navigationManager from '../managers/NavigationManager';
import studentAssignmentManager from '../managers/StudentAssignmentManager';
import studentAssignmentPlayerLauncherManager from '../managers/StudentAssignmentPlayerLauncherManager';
import studentContentCardManager from '../managers/StudentContentCardManager';
import tagContentManager from '../managers/TagContentManager';
import userManager from '../managers/UserManager';

import AssignmentService from './AssignmentService';
import ContentService from './ContentService';
import CourseService from './CourseService';
import ImageService from './ImageService';
import StudentHelperService from './StudentHelperService';

import { getRegisteredClass } from '../SatCoreRegistry';

import { getSessionStorageItem, removeSessionStorageItem } from '../utils';

/**
 * Helpers for opening and closing a student assignment/resource in one of
 * the various player modals.
 *
 * Cards (e.g., StudentAssignmentCard, StudentAssignmentTile,
 * DeprecatedStudentAssignmentCard) and the Google Classroom launch-link
 * flow call into this service.
 *
 * The service pushes state into StudentAssignmentPlayerLauncherManager so
 * the StudentAssignmentPlayerLauncher component renders the right modal.
 */
class StudentAssignmentPlayerLauncherService {
  /**
   * Open the appropriate player modal for the given student assignment.
   *
   * Resolves which player the assignment needs (LessonPlayer, FileViewer,
   * DocReader, Learnosity, etc.) and pushes the resulting state into
   * studentAssignmentPlayerLauncherManager.
   *
   * Can be called when a student clicks the primary action button on
   * a StudentAssignmentCard, StudentAssignmentTile, or
   * DeprecatedStudentAssignmentCard, and from the Google Classroom
   * launch-link flow.
   */
  static launchAssignment = async ({
    assignment,
    contentMode = CONTENT_MODE.ASSESS,
    turnInLate
  } = {}) => {
    if (studentAssignmentPlayerLauncherManager.isLaunchingAssignment) {
      return false;
    }
    studentAssignmentPlayerLauncherManager.setIsLaunchingAssignment(true);

    if (contentMode === CONTENT_MODE.ASSESS) {
      const preStartContentItemId = assignment.contentItemId;
      if (StudentAssignmentPlayerLauncherService.shouldStartActivity(assignment, contentMode)) {
        const isReady = await assignmentManager.checkActivity(assignment.activityId);
        if (isReady) {
          const isOk = await assignmentManager.startActivity(assignment.id, assignment.activityId, turnInLate);
          if (isOk === false) {
            return false;
          }
        } else {
          studentAssignmentPlayerLauncherManager.setIsLaunchingAssignment(false);
          return false;
        }
      }
      const postStartContentItemId = assignment.contentItemId;
      if (preStartContentItemId !== postStartContentItemId && tagContentManager.useContentTags) {
        tagContentManager.createNewCardContentTagFromOld(preStartContentItemId, postStartContentItemId);
      }
    }

    const option = await StudentAssignmentPlayerLauncherService.resolveContentOption({
      assignment,
      contentMode,
      isTeacher: userManager.isTeacher,
      userId: userManager.userId
    });
    const { playerType, viewUrl, isFlowpaper } = option;

    if (playerType === null && viewUrl !== null) {
      window.open(viewUrl, '_blank');
      studentAssignmentPlayerLauncherManager.setIsLaunchingAssignment(false);

      if (studentContentCardManager.isLoading) {
        studentContentCardManager.setIsLoading(false);
      }
      const assignmentLinkId = getSessionStorageItem('c2c_assignmentLinkId');
      if (assignmentLinkId) {
        removeSessionStorageItem('c2c_assignmentLinkId');
        navigationManager.setAssignmentLinkLaunched(true);
      }
      return;
    }

    contentManager.configPlayerWindow(playerType, window, StudentAssignmentPlayerLauncherService.hideIframeFromOuterClick);

    const contentImageUrl = ImageService.getImageUrl(assignment);

    if (playerType === PLAYER_TYPES.CONTENT_PREVIEW_PLAYER) {
      dialogManager.setOpenDialog(DIALOG_NAMES.CONTENT_PREVIEW, {
        resourceName: (assignment.resourceWebTitle !== '' && assignment.resourceWebTitle != null)
          ? assignment.resourceWebTitle
          : assignment.name,
        contentItemId: assignment.contentItemId,
        assignmentId: assignment.id,
        contentItemType: assignment.contentItemEntityTypeId,
        contentMode,
        instruction: assignment.activityInstruction,
        closeModalCallback: StudentAssignmentPlayerLauncherService.closeActivePlayer
      }, () => {
        dialogManager.closeDialog(DIALOG_NAMES.CONTENT_PREVIEW);
      });
      studentAssignmentPlayerLauncherManager.setIsLaunchingAssignment(false);
      return;
    }

    const { resourceWebTitle, resourceWebSubtitle } = AssignmentService.getAssignmentTitleAndDescription(assignment);

    if (playerType === PLAYER_TYPES.LESSON_PLAYER) {
      contentManager.setLessonPlayerShowing(true);
    }

    studentAssignmentPlayerLauncherManager.setActivePlayer(playerType, {
      activityId: assignment.activityId,
      assignerId: assignment.assignerId,
      assignmentId: assignment.id,
      contentImageUrl,
      contentItemId: assignment.contentItemId,
      contentMode,
      contentName: resourceWebTitle,
      contentSubtitle: resourceWebSubtitle,
      contentUrl: viewUrl,
      grade: assignment.grade,
      instruction: assignment.activityInstruction,
      isFlowpaper,
      previewContentType: assignment.contentItemEntityTypeId,
      resourceName: assignment.contentItemName
    });

    studentAssignmentPlayerLauncherManager.setIsLaunchingAssignment(false);
  }

  /**
   * Create a new student-paced assignment from the given assignment and launch it.
   * Used by the Try Again flow when the student is eligible for a fresh attempt.
   */
  static launchTryAgainStudentPacedAttempt = async ({ assignment, contentMode = CONTENT_MODE.ASSESS } = {}) => {
    if (!assignment) {
      return false;
    }
    const success = await AssignmentService.createStudentPacedAssignment(
      assignment.contentItemId,
      assignment.courseResourceElementId,
      classroomManager.currentClassroomId,
      assignment.courseContentItemId
    );
    if (!success || !assignmentManager.getLastAddedAssignment()) {
      return false;
    }
    return StudentAssignmentPlayerLauncherService.launchAssignment({
      assignment: assignmentManager.getLastAddedAssignment(),
      contentMode
    });
  }

  /** True if this assignment needs a start-activity call before opening. */
  static shouldStartActivity = (assignment, contentMode) => {
    return contentMode === CONTENT_MODE.ASSESS
      && ASSIGNMENT_STATUS.getCheckableStatuses().includes(assignment.status);
  }

  /** Ask contentManager which player and URL to use for this assignment. */
  static resolveContentOption = async ({ assignment, contentMode, isTeacher, userId }) => {
    if (contentMode === CONTENT_MODE.REVIEW) {
      return contentManager.getOptionsForStudentReview(assignment, window.location.origin, isTeacher, userId);
    }
    return contentManager.getOptionsForStudentAssessment(assignment, window.location.origin, isTeacher, userId);
  }

  /**
   * Close the active player modal and reset cross-manager side effects
   * (CONTENT_PREVIEW dialog cleanup, session-storage launch-link cleanup,
   * etc.).
   *
   * The cross-manager work lives here instead of on the manager so the
   * manager stays focused on launcher state alone.
   */
  static closeActivePlayer = async () => {
    // capture player state before clearing so we can decide whether to run
    // player-cleanup + refetch; history-listener callers fire this on every
    // route change, even when no player was actually open.
    const hasActivePlayer = !!studentAssignmentPlayerLauncherManager.activePlayer;

    const assignmentLinkId = getSessionStorageItem('c2c_assignmentLinkId');
    if (assignmentLinkId) {
      removeSessionStorageItem('c2c_assignmentLinkId');
      navigationManager.setAssignmentLinkLaunched(true);
    }

    if (dialogManager.openedDialogs?.has?.(DIALOG_NAMES.STUDENT_ASSIGNMENT)) {
      dialogManager.closeDialog(DIALOG_NAMES.STUDENT_ASSIGNMENT);
    }

    if (hasActivePlayer) {
      studentAssignmentPlayerLauncherManager.clearActivePlayer();
      // Refetch with params derived from the active menu status so TODAY correctly routes to
      // `/api/viewDueTodayActivitiesByStudent` and every other tab hits the default endpoint.
      const StudentAssignmentService = getRegisteredClass('StudentAssignmentService');
      const params = StudentAssignmentService.getFetchParamsForStudentAssignmentMenuStatus(
        studentAssignmentManager.currentStudentAssignmentMenuStatus
      );
      await StudentHelperService.fetchStudentAssignments(params);
    }
  }

  /**
   * Listener wired into contentManager.configPlayerWindow for the lesson
   * player iframe. The iframe posts `hideIframe` to close itself; this
   * handler tears down lesson-player state and removes itself from the
   * window.
   */
  static hideIframeFromOuterClick = (event) => {
    if (event.data !== 'hideIframe') {
      return;
    }
    ContentService.setLessonPlayerShowing(false);
    ContentService.setSatelliteTabShowing(false);
    ContentService.setIsTeacherPresenting(false);
    CourseService.setCourseResourceItemIdForRelatedItems(null);
    CourseService.setCourseContentItemIdForRelatedItems(null);
    CourseService.setTagIds('', true);
    StudentAssignmentPlayerLauncherService.closeActivePlayer();
    window.removeEventListener('message', StudentAssignmentPlayerLauncherService.hideIframeFromOuterClick);
  }
}

export default StudentAssignmentPlayerLauncherService;
